import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  invalidateUnionRegistrationStateCache,
  loadUnionRegistrationState,
  requestUnionMembership,
  requestUnionRegistration,
} from '../../union-registration/api/unionRegistrationApi.js';

const EMPTY_STATE = {
  profile: null,
  membership: null,
  union: null,
  matchingUnion: null,
  isPlatformAdmin: false,
};

const INITIAL_RETRY_DELAYS = [0, 500, 1000, 1800];

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function loadStateWithRetry({ attempts = INITIAL_RETRY_DELAYS, isActive = () => true } = {}) {
  let lastError = null;
  for (const delay of attempts) {
    if (!isActive()) throw new Error('entry_gate_cancelled');
    if (delay > 0) await wait(delay);
    if (!isActive()) throw new Error('entry_gate_cancelled');
    try {
      return await loadUnionRegistrationState({ force: true });
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('계정의 프로필과 유니온 상태를 확인하지 못했습니다.');
}

function resolveMode(state, loading, error) {
  if (loading) return 'loading';
  if (error) return 'error';
  if (!state.profile) return 'profile';

  const membershipStatus = String(state.membership?.status || '');
  const unionStatus = String(state.union?.registration_status || '');

  if (membershipStatus === 'active' && unionStatus === 'active') return 'union';
  if (membershipStatus === 'pending' || (membershipStatus === 'active' && unionStatus === 'pending')) return 'pending';
  if (membershipStatus === 'active' && unionStatus && unionStatus !== 'active') return 'blocked';

  return 'onboarding';
}

export function useEntryGate(sessionUserId = '') {
  const normalizedUserId = String(sessionUserId || '').trim();
  const enabled = Boolean(normalizedUserId);
  const [state, setState] = useState(EMPTY_STATE);
  const [loading, setLoading] = useState(enabled);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState({ type: '', message: '' });
  // 갤러리(갤길) / 채널(챈길). 등록 신청에 필수다.
  const [communityType, setCommunityType] = useState('');
  const stateRef = useRef(state);
  const loadRunRef = useRef(0);
  const autoJoinRef = useRef('');

  useEffect(() => { stateRef.current = state; }, [state]);

  const refresh = useCallback(async ({ silent = false, retry = false, force = true } = {}) => {
    if (!enabled) return EMPTY_STATE;
    const runId = loadRunRef.current + 1;
    loadRunRef.current = runId;
    const hadLoadedState = Boolean(stateRef.current.profile || stateRef.current.membership || stateRef.current.union);
    if (!silent && !hadLoadedState) setLoading(true);

    try {
      const next = retry
        ? await loadStateWithRetry({ isActive: () => loadRunRef.current === runId })
        : await loadUnionRegistrationState({ force });
      if (loadRunRef.current !== runId) return stateRef.current;
      setState(next);
      setError('');
      return next;
    } catch (loadError) {
      if (loadRunRef.current !== runId || loadError?.message === 'entry_gate_cancelled') return stateRef.current;
      const message = loadError instanceof Error ? loadError.message : '계정의 프로필과 유니온 상태를 확인하지 못했습니다.';
      if (!silent || !hadLoadedState) setError(message);
      return stateRef.current;
    } finally {
      if (loadRunRef.current === runId && (!silent || !hadLoadedState)) setLoading(false);
    }
  }, [enabled, normalizedUserId]);

  useEffect(() => {
    invalidateUnionRegistrationStateCache();
    loadRunRef.current += 1;
    autoJoinRef.current = '';
    setState(EMPTY_STATE);
    setWorking('');
    setError('');
    setNotice({ type: '', message: '' });

    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    const runId = loadRunRef.current + 1;
    loadRunRef.current = runId;
    setLoading(true);

    loadStateWithRetry({ isActive: () => active && loadRunRef.current === runId })
      .then((next) => {
        if (!active || loadRunRef.current !== runId) return;
        setState(next);
        setError('');
      })
      .catch((loadError) => {
        if (!active || loadRunRef.current !== runId || loadError?.message === 'entry_gate_cancelled') return;
        setError(loadError instanceof Error ? loadError.message : '계정의 프로필과 유니온 상태를 확인하지 못했습니다.');
      })
      .finally(() => {
        if (active && loadRunRef.current === runId) setLoading(false);
      });

    return () => { active = false; };
  }, [enabled, normalizedUserId]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handleProfileChanged = () => { refresh({ silent: true, force: true }); };
    const handleUnionChanged = () => { refresh({ silent: true, force: false }); };
    const handleFocus = () => { refresh({ silent: true, force: false }); };
    window.addEventListener('dororaid:game-profile-changed', handleProfileChanged);
    window.addEventListener('dororaid:union-registration-changed', handleUnionChanged);
    window.addEventListener('dororaid:union-membership-changed', handleUnionChanged);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('dororaid:game-profile-changed', handleProfileChanged);
      window.removeEventListener('dororaid:union-registration-changed', handleUnionChanged);
      window.removeEventListener('dororaid:union-membership-changed', handleUnionChanged);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, normalizedUserId, refresh]);

  const mode = useMemo(() => resolveMode(state, loading, error), [error, loading, state]);

  useEffect(() => {
    if (!enabled || mode === 'union' || mode === 'error') return undefined;
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh({ silent: true });
    };
    const timer = window.setInterval(refreshWhenVisible, 4000);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [enabled, mode, normalizedUserId, refresh]);

  const joinMatchingUnion = useCallback(async () => {
    if (working || !state.matchingUnion?.id) return false;
    setWorking('membership');
    setNotice({ type: '', message: '' });
    try {
      await requestUnionMembership(state.matchingUnion.id);
      const next = await refresh({ silent: true });
      setNotice({ type: 'success', message: `${next.union?.name || state.matchingUnion.name || '유니온'}에 자동 가입됐습니다.` });
      window.dispatchEvent(new CustomEvent('dororaid:union-membership-changed'));
      return true;
    } catch (actionError) {
      setNotice({ type: 'error', message: actionError instanceof Error ? actionError.message : '유니온 자동가입에 실패했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [refresh, state.matchingUnion, working]);

  // 2026-09-15 마이그레이션에서 커뮤니티 유형이 필수가 됐는데 이 화면만 같이 바뀌지 않아,
  // 신규 유니온 등록이 전부 community_type_required 로 실패하고 있었다(9월 중순 이후 신청 0건).
  const registerNewUnion = useCallback(async (requestedCommunityType = communityType) => {
    if (working || !state.profile?.guild_name || state.matchingUnion) return false;
    const nextCommunityType = String(requestedCommunityType || '').trim();
    if (!['gallery', 'channel'].includes(nextCommunityType)) {
      setNotice({ type: 'error', message: '갤러리(갤길)인지 채널(챈길)인지 선택해주세요.' });
      return false;
    }
    setWorking('registration');
    setNotice({ type: '', message: '' });
    try {
      const result = await requestUnionRegistration(nextCommunityType);
      await refresh({ silent: true });
      setNotice({ type: 'success', message: `${result.union_name || state.profile.guild_name} 등록 신청이 접수되었습니다.` });
      window.dispatchEvent(new CustomEvent('dororaid:union-registration-changed'));
      return true;
    } catch (actionError) {
      setNotice({ type: 'error', message: actionError instanceof Error ? actionError.message : '새 유니온 등록 신청에 실패했습니다.' });
      return false;
    } finally {
      setWorking('');
    }
  }, [communityType, refresh, state.matchingUnion, state.profile?.guild_name, working]);

  useEffect(() => {
    const matchingUnionId = state.matchingUnion?.id || '';
    if (!enabled || mode !== 'onboarding' || !matchingUnionId || working) return;
    const actionKey = `${normalizedUserId}:${state.profile?.normalized_openid || state.profile?.openid || 'profile'}:${matchingUnionId}`;
    if (autoJoinRef.current === actionKey) return;
    autoJoinRef.current = actionKey;
    joinMatchingUnion();
  }, [enabled, joinMatchingUnion, mode, normalizedUserId, state.matchingUnion?.id, state.profile?.normalized_openid, state.profile?.openid, working]);

  return {
    ...state,
    mode,
    loading,
    working,
    error,
    notice,
    refresh: (options = {}) => refresh({ retry: true, ...options }),
    joinMatchingUnion,
    registerNewUnion,
    communityType,
    setCommunityType,
  };
}
